import { createContext, useEffect, useReducer } from "react";
// CUSTOM COMPONENT
import { MatxLoading } from "app/components";
import { authClient, setAuthFailureHandler } from "app/api/api";
import { useNavigate } from "react-router-dom";

const initialState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false,
  error: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;
      return { ...state, isAuthenticated, isInitialized: true, user };
    }

    case "LOGIN": {
      return { ...state, isAuthenticated: true, user: action.payload.user };
    }

    case "LOGOUT": {
      return { ...state, isAuthenticated: false, user: null };
    }

    case "REGISTER": {
      const { user } = action.payload;
      return { ...state, user };
    }

    case "UPDATE": {
      const { user } = action.payload;
      return { ...state, user: user };
    }

    case "INIT_ERROR": {
      return {
        ...state,
        isInitialized: true,
        error: action.payload.error
      };
    }

    default:
      return state;
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => {},
  logout: () => {},
  register: () => {},
  updateUser: () => {}
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  const getLoginRouteByRole = (role) => {
    if (role === "SA") return "/session/login/admin";
    if (role === "company") return "/session/login/company";
    return "/session/login/candidate";
  };

  const login = async (email, password, type) => {
    try {
      const response = await authClient.login({ email, password, type });

      const { user } = response.data;
      if (response.status === 200) {
        dispatch({ type: "LOGIN", payload: { user } });
        return response.data;
      }
    } catch (e) {
      const res = e.response;
      if (res.status === 401 || res.status === 409) {
        return res.data;
      }
    }
  };

  const register = async (data, type) => {
    let responseData = {};
    try {
      if (type === "candidate") {
        const { jp_candidate_name, jp_email, jp_password, jp_contactno } = data;
        const response = await authClient.register(
          { jp_candidate_name, jp_email, jp_password, jp_contactno },
          type
        );

        const { user } = response;
        responseData = {
          ...user,
          role: type
        };
      }

      if (type === "company") {
        const {
          jp_company_name,
          jp_email,
          jp_password,
          jp_contact_no,
          jp_cin,
          jp_state,
          jp_industry
        } = data;
        const response = await authClient.register(
          {
            jp_company_name,
            jp_email,
            jp_password,
            jp_contact_no,
            jp_cin,
            jp_is_verified: false,
            jp_state,
            jp_industry
          },
          type
        );

        const { user } = response;
        responseData = {
          ...user,
          role: type
        };
      }

      dispatch({
        type: "REGISTER",
        payload: { user: responseData, message: "Successfully registered" }
      });
      return { error: false, message: "Successfully registered" };
    } catch (e) {
      return { error: true, message: e.response.data.message };
    }
  };

  const logout = async (roleOverride) => {
    const role = roleOverride || state.user?.role;

    try {
      await authClient.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }

    dispatch({ type: "LOGOUT" });
    navigate(getLoginRouteByRole(role));
    navigate(0);
  };

  const updateUser = (user) => {
    dispatch({ type: "UPDATE", payload: { user } });
  };

  const refreshUser = async () => {
    try {
      const res = await authClient.getProfile();
      const userData = res.data.user;

      if (userData.isDeleted) {
        await logout(userData.role);
        return;
      }

      dispatch({ type: "INIT", payload: { isAuthenticated: true, user: userData } });
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        dispatch({ type: "INIT", payload: { isAuthenticated: false, user: null } });
        return;
      }
      dispatch({ type: "INIT_ERROR", payload: { error } });
    }
  };

  useEffect(() => {
    setAuthFailureHandler(async () => {
      dispatch({ type: "LOGOUT" });
    });

    refreshUser();

    return () => {
      setAuthFailureHandler();
    };
  }, []);

  // SHOW LOADER
  if (!state.isInitialized) return <MatxLoading />;

  return (
    <AuthContext.Provider
      value={{ ...state, method: "JWT", login, logout, register, updateUser, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
